import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/lib/api';
import { absoluteImageUrl } from '@/lib/imageUrl';
import { formatEventCost } from '@/lib/formatEventCost';
import { formatEventTime } from '@/lib/formatEventTime';
import { formatEventDate } from '@/lib/formatEventDate';
import AdminEventEditModal from '@/components/AdminEventEditModal';
import ShareEventButton from '@/components/ShareEventButton';
import SEO from '@/components/SEO';
import { PremiumInstructorBadge } from '@/components/PremiumInstructorBadge';
import { Calendar, MapPin, Clock, Users, Bookmark, BookmarkCheck, Flag, ExternalLink, ArrowLeft, GraduationCap, Trophy, Sparkles, Gamepad2, Heart, X, Pencil, Copy } from 'lucide-react';

const REPORT_REASONS = [
  { value: 'false_free', label: 'Listed as free but required payment' },
  { value: 'inaccurate', label: 'Inaccurate details (date/time/location)' },
  { value: 'inappropriate', label: 'Inappropriate or offensive content' },
  { value: 'duplicate', label: 'Duplicate listing' },
  { value: 'charity_misuse', label: 'Misuse of charity status (commercial event posing as nonprofit)' },
  { value: 'other', label: 'Other' },
];

const TYPE_META = {
  teaching: { label: 'Teaching Session', icon: GraduationCap, color: '#56c6df' },
  tournament: { label: 'Tournament', icon: Trophy, color: '#df5681' },
  casual: { label: 'Open Play', icon: Gamepad2, color: '#56dfb4' },
  special: { label: 'Special Event', icon: Sparkles, color: '#b456df' },
};

export default function EventDetailPage() {
  const { eventId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('false_free');
  const [reportDetails, setReportDetails] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const isAdmin = user?.role === 'admin';
  const isHost = !!(user && event && event.host_id === user.id);
  const canEdit = isAdmin || isHost;
  const canDuplicate = canEdit;

  useEffect(() => {
    api.get(`/events/${eventId}`).then(({ data }) => { setEvent(data.event); setLoading(false); })
      .catch(() => { setLoading(false); toast.error('Event not found'); });
  }, [eventId]);

  const handleSave = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      if (event.is_saved) {
        await api.delete(`/events/${eventId}/save`);
        setEvent(p => ({ ...p, is_saved: false }));
        toast.success('Event unsaved');
      } else {
        await api.post(`/events/${eventId}/save`);
        setEvent(p => ({ ...p, is_saved: true }));
        toast.success('Event saved!');
      }
    } catch { toast.error('Failed to update'); }
  };

  const handleInterested = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      const { data } = await api.post(`/events/${eventId}/interested`);
      setEvent(p => ({ ...p, is_interested: data.interested, interested_count: data.count }));
    } catch { toast.error('Failed to update'); }
  };

  const handleReport = () => {
    if (!user) { navigate('/login'); return; }
    setReportOpen(true);
  };

  const handleDuplicate = async () => {
    setDuplicating(true);
    try {
      const { data } = await api.post(`/events/${eventId}/duplicate`);
      toast.success('Draft created — edit the date and details before publishing.');
      // Land the host on My Events where the new draft is at the top with
      // the existing "Complete Payment ($X.XX)" recovery flow ready to go.
      navigate(`/events/my-events?duplicated=${data.event.event_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to duplicate event');
    } finally {
      setDuplicating(false);
    }
  };

  const submitReport = async () => {
    setReportSubmitting(true);
    try {
      const { data } = await api.post(`/events/${eventId}/report`, {
        reason: reportReason,
        details: reportDetails.trim(),
      });
      toast.success(data?.message || 'Report submitted');
      setReportOpen(false);
      setReportReason('false_free');
      setReportDetails('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit report');
    } finally {
      setReportSubmitting(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin w-8 h-8 border-4 border-[#56dfb4] border-t-transparent rounded-full" /></div>;
  if (!event) return <div className="text-center py-20 text-slate-400">Event not found</div>;

  const meta = TYPE_META[event.event_type] || TYPE_META.casual;
  const TypeIcon = meta.icon;

  // Schema.org Event for richer search snippets + structured share
  // previews. Combine event_date + start_time into ISO when both are
  // present; otherwise fall back to date-only. endDate uses end_time
  // when the host filled it in so Google can show duration in SERPs.
  const eventStartIso = (() => {
    if (!event.event_date) return undefined;
    const d = String(event.event_date).split('T')[0];
    return event.start_time ? `${d}T${event.start_time}` : d;
  })();
  const eventEndIso = (() => {
    if (!event.event_date || !event.end_time) return undefined;
    const d = String(event.event_date).split('T')[0];
    return `${d}T${event.end_time}`;
  })();
  // Build offers for BOTH free and paid events so the page qualifies for
  // Google's Event rich-result carousel. Paid events use `cost_amount`
  // when the host entered a numeric price; if it's missing/zero we omit
  // the offers block (Google flags `offers` without a `price`).
  const paidPrice = Number(event.cost_amount);
  const eventOffers = (() => {
    if (event.is_free) {
      return {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        url: `https://meetformahjong.com/events/${event.event_id}`,
      };
    }
    if (Number.isFinite(paidPrice) && paidPrice > 0) {
      return {
        '@type': 'Offer',
        price: paidPrice.toFixed(2),
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        url: `https://meetformahjong.com/events/${event.event_id}`,
      };
    }
    return undefined;
  })();
  const eventSchema = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: event.title,
    description: (event.description || '').slice(0, 300) || `Mahjong event on Meet For Mahjong`,
    eventStatus: event.status === 'canceled'
      ? 'https://schema.org/EventCancelled'
      : 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    startDate: eventStartIso,
    endDate: eventEndIso,
    location: {
      '@type': 'Place',
      name: event.venue_name || 'TBD',
      address: {
        '@type': 'PostalAddress',
        addressLocality: event.city,
        addressRegion: event.state,
        postalCode: event.zip_code,
        addressCountry: 'US',
      },
    },
    organizer: event.host_name ? {
      '@type': 'Person',
      name: event.host_name,
      ...(event.host_id ? { url: `https://meetformahjong.com/player/${event.host_id}` } : {}),
    } : undefined,
    offers: eventOffers,
    image: event.banner_image ? absoluteImageUrl(event.banner_image) : undefined,
    url: `https://meetformahjong.com/events/${event.event_id}`,
  };
  const seoDescription = (() => {
    const parts = [];
    parts.push(meta.label);
    if (event.is_charity) parts.push('charity event');
    parts.push('in ' + [event.city, event.state].filter(Boolean).join(', '));
    if (event.event_date) parts.push('on ' + new Date(event.event_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }));
    if (event.host_name) parts.push('hosted by ' + event.host_name);
    const base = parts.filter(Boolean).join(' ').trim();
    const tail = (event.description || '').slice(0, 80).trim();
    return tail ? `${base}. ${tail}`.slice(0, 280) : `${base} — American Mahjong on Meet For Mahjong.`.slice(0, 280);
  })();

  // Keyword-rich title for long-tail searches like "Mahjong tournament
  // in [city]" or "Beginner American Mahjong class in [city]". Falls
  // back to the event title alone when location is missing.
  const seoTitle = (() => {
    const loc = [event.city, event.state].filter(Boolean).join(', ');
    if (event.is_charity && loc) return `${meta.label} (Charity Event) in ${loc} — American Mahjong`;
    if (loc) return `${meta.label} in ${loc} — American Mahjong: ${event.title}`;
    return `${event.title} — ${meta.label}, American Mahjong`;
  })();

  return (
    <div data-testid="event-detail-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <SEO
        title={seoTitle}
        description={seoDescription}
        path={`/events/${event.event_id}`}
        image={event.banner_image ? absoluteImageUrl(event.banner_image) : undefined}
        jsonLd={eventSchema}
      />
      <Link to="/events" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Events
      </Link>

      {/* Banner image (full-size) */}
      {event.banner_image && (
        <div className="w-full aspect-[16/6] overflow-hidden rounded-2xl mb-4 bg-slate-100">
          <img
            src={absoluteImageUrl(event.banner_image)}
            alt={event.title}
            decoding="async"
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Header bar */}
      <div className="h-2 rounded-t-2xl" style={{ backgroundColor: meta.color }} />
      <Card className="rounded-t-none border-slate-100 mb-6">
        <CardContent className="p-8">
          <div className="flex flex-wrap items-start gap-3 mb-4">
            <Badge className="rounded-full text-white text-sm px-3 py-1 gap-1" style={{ backgroundColor: meta.color }}>
              <TypeIcon className="w-3.5 h-3.5" /> {meta.label}
            </Badge>
            {event.is_free ? (
              <Badge className="rounded-full bg-[#56dfb4]/15 text-[#0f172a] text-sm px-3 py-1">FREE</Badge>
            ) : (
              <Badge variant="outline" className="rounded-full text-sm px-3 py-1">{formatEventCost(event)}</Badge>
            )}
            {event.is_charity && (
              <Badge className="rounded-full bg-[#df5681]/15 text-[#df5681] border border-[#df5681]/40 text-sm px-3 py-1 gap-1" data-testid="event-charity-badge">
                <Heart className="w-3.5 h-3.5" /> Charity Event
              </Badge>
            )}
            {event.is_destination_event && (
              <Badge className="rounded-full bg-[#56c6df]/15 text-[#56c6df] border border-[#56c6df]/40 text-sm px-3 py-1 gap-1" data-testid="event-destination-badge">
                🌍 Destination Event
              </Badge>
            )}
            {event.status === 'canceled' && <Badge variant="destructive" className="rounded-full">Canceled</Badge>}
          </div>

          <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>{event.title}</h1>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-slate-600">
                <Calendar className="w-4 h-4 text-[#56c6df]" />
                <span>{event.event_date ? formatEventDate(event.event_date, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'TBD'}</span>
              </div>
              <div className="flex items-start gap-2 text-slate-600">
                <Clock className="w-4 h-4 text-[#56c6df] mt-1" />
                <div>
                  <p>{formatEventTime(event.start_time, event.end_time, event.timezone)}</p>
                  <p className="text-[11px] text-slate-400">Venue local time</p>
                </div>
              </div>
              {event.is_recurring && (
                <div className="text-xs text-[#b456df] font-medium">Recurring: {event.recurring_frequency}</div>
              )}
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-slate-600">
                <MapPin className="w-4 h-4 text-[#df5681]" />
                <div>
                  <p className="font-medium">{event.venue_name}</p>
                  <p className="text-sm text-slate-500">{event.street_address && `${event.street_address}, `}{event.city}, {event.state} {event.zip_code}</p>
                  <span className="text-xs capitalize text-slate-400">{event.location_type === 'public' ? 'Public Venue' : event.location_type === 'online' ? 'Online/Virtual' : 'Private Residence'}</span>
                </div>
              </div>
              {event.max_attendees && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Users className="w-4 h-4 text-[#56dfb4]" />
                  <span>Max {event.max_attendees} attendees</span>
                </div>
              )}
            </div>
          </div>

          {event.is_charity && event.charity_nonprofit_name && (
            <div className="mb-6 p-4 rounded-xl border border-[#df5681]/30 bg-[#df5681]/5" data-testid="event-charity-block">
              <h3 className="font-semibold text-sm mb-2 flex items-center gap-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Heart className="w-4 h-4 text-[#df5681]" /> Benefits {event.charity_nonprofit_name}
              </h3>
              {event.charity_description && (
                <p className="text-sm text-slate-700 mb-2">{event.charity_description}</p>
              )}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                {event.charity_website && (
                  <a href={event.charity_website} target="_blank" rel="noopener noreferrer" className="text-[#df5681] hover:underline" data-testid="event-charity-website-link">
                    Visit charity website →
                  </a>
                )}
                {event.charity_flyer_url && (
                  <a href={event.charity_flyer_url} target="_blank" rel="noopener noreferrer" className="text-[#df5681] hover:underline" data-testid="event-charity-flyer-link">
                    View event flyer →
                  </a>
                )}
              </div>
            </div>
          )}

          <div className="mb-6">
            <h3 className="font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Description</h3>
            <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">{event.description}</p>
          </div>

          {event.additional_notes && (
            <div className="mb-6 p-4 rounded-xl bg-slate-50">
              <h3 className="font-semibold text-sm mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Additional Notes</h3>
              <p className="text-sm text-slate-600">{event.additional_notes}</p>
            </div>
          )}

          <div className="flex flex-wrap gap-2 mb-6">
            <span className="text-sm text-slate-500 mr-2">Skill Levels:</span>
            {(event.skill_levels || []).map(s => (
              <span key={s} className="px-3 py-1 rounded-full bg-slate-100 text-xs capitalize font-medium">{s}</span>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-6 text-sm" data-testid="event-host-row">
            <span className="text-slate-500">Hosted by:</span>
            {event.host_profile_linkable && event.host_id ? (
              <Link
                to={`/player/${event.host_id}`}
                className="font-medium text-[#56c6df] hover:underline inline-flex items-center gap-1.5"
                data-testid="event-host-link"
              >
                {event.host_name}
                {event.host_is_instructor ? (
                  <span
                    className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#b456df]/15 text-[#7d2eaf]"
                    data-testid="event-host-instructor-badge"
                  >
                    Instructor
                  </span>
                ) : (
                  <span
                    className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600"
                    data-testid="event-host-player-badge"
                  >
                    Player
                  </span>
                )}
              </Link>
            ) : (
              <span className="font-medium" data-testid="event-host-name-plain">{event.host_name}</span>
            )}
            {event.host_is_premium && (
              <PremiumInstructorBadge size="sm" data-testid="event-host-premium-badge" />
            )}
            <span className="text-slate-400">|</span>
            <a href={`mailto:${event.contact_email}`} className="text-[#56c6df] hover:underline">{event.contact_email}</a>
          </div>
          {event.contact_email && !isHost && (
            <p
              className="text-[11px] text-slate-500 italic -mt-4 mb-6 leading-snug"
              data-testid="event-mention-mfm-note"
            >
              <span className="not-italic">💬</span> When you reach out, please mention you found this event on <span className="font-semibold text-[#56c6df] not-italic">Meet For Mahjong</span> — it helps hosts see they're getting recognized here.
            </p>
          )}

          {event.external_link && (
            <a href={event.external_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#b456df] hover:underline text-sm mb-6">
              <ExternalLink className="w-3.5 h-3.5" /> External Link
            </a>
          )}

          {event.signup_instructions && (
            <div className="mb-6 p-4 rounded-xl bg-[#56c6df]/5 border border-[#56c6df]/20">
              <h3 className="font-semibold text-sm mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>How to Sign Up & Pay</h3>
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{event.signup_instructions}</p>
            </div>
          )}

          {/* Interested Counter */}
          <div className="flex items-center gap-4 mb-4 p-4 rounded-xl bg-slate-50">
            <div className="text-center">
              <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{event.interested_count || 0}</p>
              <p className="text-xs text-slate-500">Interested</p>
            </div>
            {user && (
              <Button onClick={handleInterested} variant={event.is_interested ? 'default' : 'outline'}
                className={`rounded-full gap-2 ${event.is_interested ? 'bg-[#df5681] text-white hover:bg-[#c44570]' : ''}`}
                data-testid="interested-event-btn">
                <Heart className={`w-4 h-4 ${event.is_interested ? 'fill-current' : ''}`} />
                {event.is_interested ? "I'm Interested" : 'Interested?'}
              </Button>
            )}
          </div>

          <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-100">
            {user ? (
              <Button onClick={handleSave} variant={event.is_saved ? 'default' : 'outline'}
                className={`rounded-full gap-2 ${event.is_saved ? 'bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b]' : ''}`}
                data-testid="save-event-btn">
                {event.is_saved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                {event.is_saved ? 'Saved' : 'Save This Event'}
              </Button>
            ) : (
              // Anon viewers see the Save button too — clicking it routes to
              // /register with a deep-link back to this event. We keep the
              // button visually present (not greyed out) so the affordance
              // reads as a real, accessible feature; the `title` tooltip
              // makes the price-of-entry obvious on hover.
              <Link to={`/register?next=/events/${eventId}`} data-testid="anon-save-event-link">
                <Button
                  variant="outline"
                  className="rounded-full gap-2 border-dashed text-slate-600 hover:border-[#56dfb4] hover:text-[#56dfb4]"
                  title="Sign up free to save events to your favorites"
                  data-testid="anon-save-event-btn"
                >
                  <Bookmark className="w-4 h-4" />
                  Save This Event
                  <span className="ml-1 text-[10px] font-bold uppercase tracking-wider bg-[#56dfb4]/15 text-[#3ec49b] px-1.5 py-0.5 rounded-full">
                    Free signup
                  </span>
                </Button>
              </Link>
            )}
            <ShareEventButton event={event} />
            {canEdit && (
              <Button
                variant="outline"
                onClick={() => setEditing(true)}
                className="rounded-full gap-2 border-[#b456df] text-[#b456df] hover:bg-[#b456df]/10"
                data-testid="event-edit-btn"
              >
                <Pencil className="w-4 h-4" /> {isAdmin && !isHost ? 'Admin Edit' : 'Edit Event'}
              </Button>
            )}
            {canDuplicate && (
              <Button
                variant="outline"
                onClick={handleDuplicate}
                disabled={duplicating}
                className="rounded-full gap-2 border-[#56c6df] text-[#56c6df] hover:bg-[#56c6df]/10"
                data-testid="event-duplicate-btn"
                title="Create an editable copy of this event as a new draft"
              >
                <Copy className={`w-4 h-4 ${duplicating ? 'animate-pulse' : ''}`} />
                {duplicating ? 'Duplicating…' : 'Duplicate'}
              </Button>
            )}
            {user && (
              <Button variant="ghost" onClick={handleReport} className="rounded-full text-slate-400 hover:text-red-500 gap-1" data-testid="report-event-btn">
                <Flag className="w-4 h-4" /> Report
              </Button>
            )}
          </div>

          {!user && (
            <div className="mt-4 p-4 rounded-2xl bg-slate-50 border border-slate-100 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center" data-testid="anon-event-cta">
              <p className="text-sm text-slate-600 flex-1">
                Sign up free to save this event, mark yourself interested, and message the host.
              </p>
              <div className="flex gap-2">
                <Link to={`/login?next=/events/${eventId}`}>
                  <Button variant="outline" className="rounded-full" data-testid="anon-event-login-btn">Log in</Button>
                </Link>
                <Link to="/register">
                  <Button className="rounded-full bg-[#56c6df] text-white hover:bg-[#3eb0c9]" data-testid="anon-event-signup-btn">Sign up free</Button>
                </Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {editing && (
        <AdminEventEditModal
          event={event}
          mode={isHost && !isAdmin ? 'host' : 'admin'}
          onClose={() => setEditing(false)}
          onSaved={updated => setEvent(prev => ({ ...prev, ...updated }))}
        />
      )}

      {/* Report Event Modal */}
      {reportOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="report-event-modal" onClick={() => setReportOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Report this event</h3>
              <button onClick={() => setReportOpen(false)} className="p-1 hover:bg-slate-100 rounded-full" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-3">Help us keep event listings accurate. Your report is sent to the admins for review.</p>
            <div className="space-y-2 mb-4">
              {REPORT_REASONS.map(r => (
                <label key={r.value} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${reportReason === r.value ? 'border-[#df5681] bg-[#df5681]/5' : 'border-slate-200 hover:border-slate-300'}`}>
                  <input
                    type="radio"
                    name="report-reason"
                    value={r.value}
                    checked={reportReason === r.value}
                    onChange={() => setReportReason(r.value)}
                    className="accent-[#df5681]"
                    data-testid={`report-reason-${r.value}`}
                  />
                  <span className="text-sm">{r.label}</span>
                </label>
              ))}
            </div>
            <Textarea
              value={reportDetails}
              onChange={e => setReportDetails(e.target.value)}
              placeholder="Optional — share any extra details (e.g., the amount of money required)"
              rows={3}
              maxLength={500}
              className="rounded-xl"
              data-testid="report-details-input"
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" onClick={() => setReportOpen(false)} className="rounded-full">Cancel</Button>
              <Button
                onClick={submitReport}
                disabled={reportSubmitting}
                className="rounded-full bg-[#df5681] text-white hover:bg-[#c44570] px-6"
                data-testid="submit-report-btn"
              >
                {reportSubmitting ? 'Submitting…' : 'Submit Report'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
